import { pythonApi } from '../../../app/services/api/api'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import {
  DeviceModelDiscovery,
  DeviceResponse,
  DeviceGroupListType,
  GroupQueryParams,
  DeviceLiteResponse,
  DeviceCreateRequest,
  DeviceDiscovery,
  DeviceLite,
} from './types'

const extendedApi = pythonApi.injectEndpoints({
  endpoints: (builder) => ({
    getDevicesByEmail: builder.query<DeviceDiscovery, { email: string }>({
      query: ({ email }) => {
        const queryParams = new URLSearchParams()

        if (email) {
          queryParams.set('email', email)
        }

        return {
          url: `/device-auto-discovery/?${queryParams}`,
          method: 'GET',
        }
      },
      providesTags: ['DeviceAutoDiscovery'],
    }),
    getDeviceModels: builder.query<DeviceModelDiscovery, void>({
      query: () => ({
        url: '/device-models/',
        method: 'GET',
      }),
      providesTags: ['ListDeviceConfig'],
    }),
    getDevicesLite: builder.query<DeviceLite[], { tenantId?: number }>({
      query: ({ tenantId }) => {
        const queryParams = new URLSearchParams()

        if (tenantId) {
          queryParams.set('tenant', tenantId.toString())
        }

        return {
          url: `/devices-lite/?${queryParams.toString()}`,
          method: 'GET',
        }
      },
      providesTags: ['ListDeviceConfig'],
    }),
    createDevice: builder.mutation<DeviceLiteResponse, DeviceCreateRequest>({
      query: (body) => ({
        url: `/devices-lite/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ListDeviceConfig', 'DeviceAutoDiscovery'],
    }),
    deleteDevices: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `/devices-lite/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ListDeviceConfig', 'DeviceAutoDiscovery'],
    }),
    updateDevice: builder.mutation<
      DeviceLiteResponse,
      {
        id: number
        physical_id: any
        location: number | string
        model: number
        device_channels: { model_channel_id: number; title: string }[]
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/devices-lite/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ListDeviceConfig'],
    }),
    getLocationDevices: builder.query<any, void>({
      query: () => '/location-device/',
    }),
    getDepartmentsByTenant: builder.query<any, { tenant_id: number }>({
      query: ({ tenant_id }) => {
        const queryParams = new URLSearchParams()
        tenant_id && queryParams.set('tenant_id', tenant_id.toString())

        return {
          url: `/department/?${queryParams.toString()}`,
          method: 'GET',
        }
      },
      providesTags: ['List'],
    }),
    getUsersByTenants: builder.query<any, { tenant_id: number }>({
      query: ({ tenant_id }) => {
        return {
          url: `/tenant/${tenant_id}/users/`,
          method: 'GET',
        }
      },
      providesTags: ['ListTenantUsers'],
    }),
    inviteUser: builder.mutation<any, any>({
      query: (body) => ({
        url: `/invite-user/`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['ListTenantUsers'],
    }),
    updateUserLocations: builder.mutation<
      { user_id: number; updated_location_ids: number[] },
      { tenant_id: number; user_id: number; location_ids: number[] }
    >({
      query: ({ tenant_id, user_id, location_ids }) => ({
        url: `/tenant/${tenant_id}/users/${user_id}/locations/`,
        method: 'PATCH',
        body: { location_ids },
      }),
      invalidatesTags: ['ListTenantUsers'],
    }),
    getDeviceCountByUserEmail: builder.query<number, string | undefined>({
      async queryFn(email, _queryApi, _extraOptions, _baseQuery) {
        if (!email) {
          return { data: 0 }
        }

        try {
          const token = localStorage.getItem('aiot_t')
          if (!token) {
            return { data: 0 }
          }
          const response = await fetch('/device-auto-discovery/', {
            headers: {
              Authorization: `Token ${token}`,
            },
          })

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: response.statusText,
              } as FetchBaseQueryError,
            }
          }

          const data: DeviceResponse = await response.json()
          const filteredDevices = data.results.filter((device) => device.email === email)

          return { data: filteredDevices.length }
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
            } as FetchBaseQueryError,
          }
        }
      },
    }),
    createDeviceGroup: builder.mutation<any, { name: string; device_ids: number[] }>({
      query: (body) => {
        return {
          url: `/devices-groups/`,
          method: 'POST',
          body: body,
        }
      },
      invalidatesTags: ['DeviceGroups'],
    }),

    deleteDeviceGroup: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/devices-groups/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DeviceGroups'],
    }),

    updateDeviceGroup: builder.mutation<
      any,
      { id: number; name: string; device_ids: number[] }
    >({
      query: (body) => {
        return {
          url: `/devices-groups/${body.id}/`,
          method: 'PATCH',
          body: body,
        }
      },
      invalidatesTags: ['DeviceGroups'],
    }),
    getDeviceGroups: builder.query<any, void>({
      query: () => '/devices-groups/',
      providesTags: ['DeviceGroups'],
    }),
    getDeviceGroupList: builder.query<DeviceGroupListType[], GroupQueryParams>({
      query: (props: GroupQueryParams) => {
        const queryParams = new URLSearchParams()
        if (props.measurementTypeId) {
          queryParams.set('measurement_type', props.measurementTypeId)
        }
        return {
          url: `/devices-groups/?${queryParams.toString()}`,
          method: 'GET',
        }
      },
      providesTags: ['List'],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetUsersByTenantsQuery,
  useInviteUserMutation,
  useUpdateUserLocationsMutation,
  useGetDepartmentsByTenantQuery,
  useGetDeviceCountByUserEmailQuery,
  useGetDeviceModelsQuery,
  useCreateDeviceGroupMutation,
  useDeleteDeviceGroupMutation,
  useUpdateDeviceGroupMutation,
  useGetDeviceGroupListQuery,
  useGetDeviceGroupsQuery,
  useGetLocationDevicesQuery,
  useCreateDeviceMutation,
  useUpdateDeviceMutation,
  useDeleteDevicesMutation,
  useGetDevicesByEmailQuery,
  useGetDevicesLiteQuery,
} = extendedApi
